/**
 * @fileoverview Faixa da abordagem inteligente na conversa, entre as mensagens
 * e o campo de digitar: o estado, quando sai a próxima mensagem, "Cancelar
 * fluxo inteligente" (o atendimento continua) e "Detalhes" (a linha do tempo).
 * O tempo restante é recalculado a cada minuto. Encerrada, fica 24 h, com um
 * "x" para esconder (só para quem clicou, neste navegador).
 */

import React, { useCallback, useContext, useEffect, useState } from "react";
import clsx from "clsx";
import { CircleAlert, CircleCheck, Sparkles, X } from "lucide-react";

import { AuthContext } from "../../context/Auth/AuthContext";
import { runView } from "../../utils/smartApproachText";
import CancelRunDialog from "./CancelRunDialog";
import SmartApproachDetails from "./SmartApproachDetails";
import useStyles from "./styles";
import { formatAt, tr } from "./text";

const DISMISSED_MAX = 50;
const dismissedKey = (userId) => `smartApproachBar:dismissed:${userId || "anon"}`;

const readDismissed = (userId) => {
  try {
    const list = JSON.parse(localStorage.getItem(dismissedKey(userId)) || "[]");
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
};

const writeDismissed = (userId, list) => {
  try {
    localStorage.setItem(dismissedKey(userId), JSON.stringify(list.slice(-DISMISSED_MAX)));
  } catch {
    /* sem localStorage: o "x" vale até recarregar a página */
  }
};

const TONE_ICON = { ok: CircleCheck, bad: CircleAlert };

const SmartApproachBar = ({ run, onReload, onApplyRun }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const userId = user?.id;
  const [, setTick] = useState(0);
  const [dismissed, setDismissed] = useState(() => readDismissed(userId));
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => setTick((n) => n + 1), 60 * 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    setDismissed(readDismissed(userId));
  }, [userId]);

  const dismiss = useCallback(() => {
    const next = [...readDismissed(userId).filter((id) => id !== run.id), run.id];
    writeDismissed(userId, next);
    setDismissed(next);
  }, [userId, run.id]);

  // Cancelada: mostra a resposta da API; sem ela (erro), relê.
  const afterCancel = useCallback((next) => (next ? onApplyRun(next) : onReload()), [onApplyRun, onReload]);

  const view = runView(run, { now: Date.now(), t: tr, formatAt });
  const hidden = !view || (view.phase !== "pending" && dismissed.includes(run.id));
  const Icon = (view && TONE_ICON[view.tone]) || Sparkles;

  return (
    <>
      {!hidden && (
        <div className={classes.bar} role="status" data-smart-approach={view.phase}>
          <span className={clsx(classes.icon, view.tone !== "neutral" && classes[`tone_${view.tone}`])} aria-hidden="true">
            <Icon />
          </span>
          <div className={classes.text}>
            <span className={classes.title}>{view.title}</span>
            {" · "}
            <span className={clsx(classes.detail, view.tone !== "neutral" && classes[`tone_${view.tone}`])}>{view.detail}</span>
            {view.phase === "pending" && <span className={classes.hint}>{tr("bar.hint")}</span>}
          </div>
          <div className={classes.actions}>
            <button type="button" className={classes.btn} onClick={() => setDetailsOpen(true)}>
              {tr("bar.details")}
            </button>
            {view.phase === "pending" ? (
              <button type="button" className={classes.btn} onClick={() => setConfirmOpen(true)}>
                {tr("bar.cancel")}
              </button>
            ) : (
              <button type="button" className={classes.iconBtn} onClick={dismiss} aria-label={tr("bar.dismiss")} title={tr("bar.dismiss")}>
                <X />
              </button>
            )}
          </div>
        </div>
      )}
      <SmartApproachDetails
        run={run}
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        onReload={onReload}
        onCancel={() => setConfirmOpen(true)}
      />
      <CancelRunDialog runId={run.id} open={confirmOpen} onClose={() => setConfirmOpen(false)} onCancelled={afterCancel} />
    </>
  );
};

export default SmartApproachBar;
